import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Badge,
  Button,
  Card,
  ColorPicker,
  Container,
  FileUpload,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  parseColor,
  Portal,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select';
import WorkspaceLayout from './workspace-layout';
import useWorkspace from '@/lib/hooks/use-workspace';
import { useAuth } from '@/lib/hooks/use-auth';
import useIsDefaultWorkspace from '@/lib/hooks/use-is-default-workspace';
import useWorkspaceStore from '@/utils/use-workspace';
import PartnerContent from './partner-content';
import { COUNTRIES } from '@/lib/country';
import api from '@/utils/api';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  country: z.string().optional(),
  set_default: z.boolean().optional(),
  is_public: z.boolean().optional(),
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

type ImageFieldProps = {
  label: string;
  helperText: string;
  url: string | null;
  isUploading: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onAccept: (file: { files: File[] }) => void;
  onRemove: () => void;
  previewWidth: string;
  previewHeight: string;
  previewBg?: string;
  maxFileSize: number;
};

function ImageField({
  label,
  helperText,
  url,
  isUploading,
  inputRef,
  onAccept,
  onRemove,
  previewWidth,
  previewHeight,
  previewBg = 'bg.muted',
  maxFileSize,
}: ImageFieldProps) {
  return (
    <Box>
      <Text fontWeight='semibold' mb={2}>
        {label}
      </Text>
      <Flex direction={{ base: 'column', sm: 'row' }} gap={4} align={{ sm: 'center' }}>
        <Box
          width={previewWidth}
          height={previewHeight}
          flexShrink={0}
          borderRadius='md'
          overflow='hidden'
          borderWidth={1}
          borderColor='border'
          bg={previewBg}
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          {url ? (
            <Image src={url} alt={label} width='100%' height='100%' objectFit='cover' />
          ) : (
            <Text fontSize='xs' color='fg.muted'>
              No image
            </Text>
          )}
        </Box>
        <Stack gap={3} flex={1}>
          <Text fontSize='sm' color='fg.muted'>
            {helperText}
          </Text>
          <FileUpload.Root
            accept={ACCEPTED_IMAGE_TYPES}
            maxFileSize={maxFileSize}
            disabled={isUploading}
            onFileAccept={(file) => {
              if (file) onAccept(file);
            }}
          >
            <FileUpload.HiddenInput ref={inputRef} />
            <HStack gap={2}>
              <FileUpload.Trigger asChild>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  loading={isUploading}
                  loadingText='Uploading...'
                  disabled={isUploading}
                >
                  {url ? (
                    'Replace'
                  ) : (
                    <>
                      <Icon as={ArrowUpTrayIcon} h={4} w={4} />
                      Upload image
                    </>
                  )}
                </Button>
              </FileUpload.Trigger>
              {url && (
                <Button
                  type='button'
                  size='sm'
                  variant='ghost'
                  colorPalette='red'
                  onClick={onRemove}
                  disabled={isUploading}
                >
                  Remove
                </Button>
              )}
            </HStack>
          </FileUpload.Root>
        </Stack>
      </Flex>
    </Box>
  );
}

export default function EditAccount() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const isDefaultWorkspace = useIsDefaultWorkspace();
  const { changeWorkspace, changeDefaultWorkspaceId } = useAuth();
  const { workspace } = useWorkspace();
  const { loadWorkspaces } = useWorkspaceStore();

  const [logoUrl, setLogoUrl] = useState<string | null>(workspace?.logo || null);
  const [bannerLogoUrl, setBannerLogoUrl] = useState<string | null>(workspace?.banner_logo || null);
  const [bannerImageUrl, setBannerImageUrl] = useState<string | null>(workspace?.banner_image || null);

  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isBannerLogoUploading, setIsBannerLogoUploading] = useState(false);
  const [isBannerUploading, setIsBannerUploading] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerLogoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const isPublic = watch('is_public');

  const onSubmit = async (data: FormData) => {
    try {
      const response = await api.put(`workspace/${workspaceId}`, {
        name: data.name,
        description: data.description,
        country: data.country,
        set_default: data.set_default,
        is_public: data.is_public,
        primary_color: data.primary_color,
        secondary_color: data.secondary_color,
        logo: logoUrl,
        banner_logo: bannerLogoUrl,
        banner_image: bannerImageUrl,
      });

      const resData = response.data.data;
      const updatedWorkspace = {
        ...workspace,
        name: resData.name,
        description: resData.description,
        country: resData.country,
        is_public: resData.is_public,
        is_base: resData.set_default,
        primary_color: resData.primary_color,
        secondary_color: resData.secondary_color,
        logo: resData.logo,
        banner_logo: resData.banner_logo,
        banner_image: resData.banner_image,
      };
      changeWorkspace(updatedWorkspace);
      if (workspaceId && resData.set_default) {
        changeDefaultWorkspaceId(parseInt(workspaceId));
      }
      toaster.create({
        type: 'success',
        title: 'Workspace settings saved',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update workspace',
      });
    }
  };

  const uploadImage = async (
    file: { files: File[] },
    endpoint: 'logo' | 'banner-logo' | 'banner',
    fieldName: 'logo' | 'banner_logo' | 'banner',
    pathField: 'logo_path' | 'banner_logo_path' | 'banner_path',
    setUrl: (url: string) => void,
    setLoading: (loading: boolean) => void,
    workspaceField: 'logo' | 'banner_logo' | 'banner_image',
    successMsg: string,
  ) => {
    if (!file.files || file.files.length === 0) return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append(fieldName, file.files[0]);
      const response = await api.post(`workspace/${workspaceId}/${endpoint}/upload`, formData);
      const url = response.data?.data?.[pathField];
      if (url) {
        setUrl(url);
        if (workspace) {
          changeWorkspace({ ...workspace, [workspaceField]: url });
        }
        loadWorkspaces();
      }
      toaster.create({ type: 'success', title: successMsg });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to upload image',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workspace && isDefaultWorkspace !== undefined) {
      reset({
        name: workspace.name,
        description: workspace.description,
        country: workspace.country || '',
        set_default: isDefaultWorkspace,
        is_public: workspace.is_public,
        primary_color: workspace.primary_color || '#09090B',
        secondary_color: workspace.secondary_color || '#f7f5f2',
      });
      setLogoUrl(workspace.logo || null);
      setBannerLogoUrl(workspace.banner_logo || null);
      setBannerImageUrl(workspace.banner_image || null);
    }
  }, [workspace, isDefaultWorkspace, reset]);

  return (
    <WorkspaceLayout>
      <Container maxW='4xl' py={6}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={6}>
            <Box>
              <Heading size='2xl'>Edit workspace settings</Heading>
              <Text mt={2} color='fg.muted'>
                Manage your workspace details, branding, and content sources.
              </Text>
            </Box>

            <Card.Root>
              <Card.Header>
                <Card.Title>Workspace details</Card.Title>
                <Card.Description>
                  Basic info about your workspace and how it&apos;s shared.
                </Card.Description>
              </Card.Header>
              <Card.Body>
                <Stack gap={5}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                    <Field
                      label='Workspace name'
                      invalid={!!errors.name}
                      errorText={errors.name?.message}
                    >
                      <Input {...register('name')} />
                    </Field>
                    <Field label='Country'>
                      <NativeSelectRoot>
                        <NativeSelectField {...register('country')} placeholder='Select a country'>
                          {COUNTRIES.map((country) => (
                            <option key={country.value} value={country.label}>
                              {country.label}
                            </option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Field>
                  </SimpleGrid>
                  <Field label='Description'>
                    <Textarea rows={3} {...register('description')} />
                  </Field>

                  <Box pt={4} borderTopWidth={1} borderColor='border'>
                    <Controller
                      control={control}
                      name='is_public'
                      render={({ field }) => (
                        <Stack gap={2}>
                          <Flex justify='space-between' align='center' gap={3}>
                            <HStack gap={2}>
                              <Text fontWeight='semibold'>Content sharing</Text>
                              <Badge
                                size='xs'
                                variant='subtle'
                                colorPalette={isPublic ? 'green' : 'gray'}
                              >
                                {isPublic ? 'PUBLIC' : 'PRIVATE'}
                              </Badge>
                            </HStack>
                            <Switch.Root
                              checked={!!field.value}
                              onCheckedChange={(e) => field.onChange(e.checked)}
                              colorPalette='brand'
                            >
                              <Switch.HiddenInput />
                              <Switch.Control>
                                <Switch.Thumb />
                              </Switch.Control>
                            </Switch.Root>
                          </Flex>
                          <Text fontSize='sm' color='fg.muted'>
                            When public, your workspace&apos;s content can appear in other
                            workspaces&apos; Partner content libraries. When private, only members
                            of this workspace can see it.
                          </Text>
                        </Stack>
                      )}
                    />
                  </Box>

                  <Controller
                    control={control}
                    name='set_default'
                    render={({ field }) => (
                      <Field disabled={isDefaultWorkspace}>
                        <Checkbox
                          colorPalette='brand'
                          checked={field.value}
                          onCheckedChange={({ checked }) => field.onChange(checked)}
                        >
                          Set as my default workspace
                        </Checkbox>
                      </Field>
                    )}
                  />
                </Stack>
              </Card.Body>
            </Card.Root>

            <Card.Root>
              <Card.Header>
                <Card.Title>Workspace branding</Card.Title>
                <Card.Description>
                  Customize how your workspace looks for everyone who visits it.
                </Card.Description>
              </Card.Header>
              <Card.Body>
                <Stack gap={6}>
                  <ImageField
                    label='Header logo'
                    helperText='Shown in the top-left of every page. Recommended: 250×100px, horizontal orientation.'
                    url={logoUrl}
                    isUploading={isLogoUploading}
                    inputRef={logoInputRef}
                    onAccept={(file) =>
                      uploadImage(
                        file,
                        'logo',
                        'logo',
                        'logo_path',
                        setLogoUrl,
                        setIsLogoUploading,
                        'logo',
                        'Header logo updated',
                      )
                    }
                    onRemove={() => setLogoUrl(null)}
                    previewWidth='100px'
                    previewHeight='100px'
                    maxFileSize={5 * 1024 * 1024}
                  />
                  <ImageField
                    label='Banner logo'
                    helperText='Featured on your workspace home page over the banner image. Recommended: 500×500px, square orientation.'
                    url={bannerLogoUrl}
                    isUploading={isBannerLogoUploading}
                    inputRef={bannerLogoInputRef}
                    onAccept={(file) =>
                      uploadImage(
                        file,
                        'banner-logo',
                        'banner_logo',
                        'banner_logo_path',
                        setBannerLogoUrl,
                        setIsBannerLogoUploading,
                        'banner_logo',
                        'Banner logo updated',
                      )
                    }
                    onRemove={() => setBannerLogoUrl(null)}
                    previewWidth='100px'
                    previewHeight='100px'
                    maxFileSize={5 * 1024 * 1024}
                  />
                  <ImageField
                    label='Banner image'
                    helperText='Background image behind the banner logo on the home page. Recommended: 1600×400px, horizontal orientation.'
                    url={bannerImageUrl}
                    isUploading={isBannerUploading}
                    inputRef={bannerInputRef}
                    onAccept={(file) =>
                      uploadImage(
                        file,
                        'banner',
                        'banner',
                        'banner_path',
                        setBannerImageUrl,
                        setIsBannerUploading,
                        'banner_image',
                        'Banner image updated',
                      )
                    }
                    onRemove={() => setBannerImageUrl(null)}
                    previewWidth='160px'
                    previewHeight='80px'
                    maxFileSize={10 * 1024 * 1024}
                  />

                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                    <Field
                      label='Primary button color'
                      helperText='Used for primary buttons and active links. We recommend a dark color so white button text stays readable.'
                    >
                      <Controller
                        name='primary_color'
                        control={control}
                        render={({ field }) => (
                          <ColorPicker.Root
                            name={field.name}
                            value={parseColor(field.value || '#09090B')}
                            onValueChange={(e) => field.onChange(e.value.toString('hex'))}
                          >
                            <ColorPicker.HiddenInput />
                            <ColorPicker.Control>
                              <ColorPicker.Input />
                              <ColorPicker.Trigger />
                            </ColorPicker.Control>
                            <Portal>
                              <ColorPicker.Positioner>
                                <ColorPicker.Content>
                                  <ColorPicker.Area />
                                  <HStack>
                                    <ColorPicker.EyeDropper size='sm' variant='outline' />
                                    <ColorPicker.Sliders />
                                  </HStack>
                                </ColorPicker.Content>
                              </ColorPicker.Positioner>
                            </Portal>
                          </ColorPicker.Root>
                        )}
                      />
                    </Field>
                    <Field
                      label='Page background color'
                      helperText='Used as the background for all workspace pages. We recommend a light tone so content stays easy to read.'
                    >
                      <Controller
                        name='secondary_color'
                        control={control}
                        render={({ field }) => (
                          <ColorPicker.Root
                            name={field.name}
                            value={parseColor(field.value || '#f7f5f2')}
                            onValueChange={(e) => field.onChange(e.value.toString('hex'))}
                          >
                            <ColorPicker.HiddenInput />
                            <ColorPicker.Control>
                              <ColorPicker.Input />
                              <ColorPicker.Trigger />
                            </ColorPicker.Control>
                            <Portal>
                              <ColorPicker.Positioner>
                                <ColorPicker.Content>
                                  <ColorPicker.Area />
                                  <HStack>
                                    <ColorPicker.EyeDropper size='sm' variant='outline' />
                                    <ColorPicker.Sliders />
                                  </HStack>
                                </ColorPicker.Content>
                              </ColorPicker.Positioner>
                            </Portal>
                          </ColorPicker.Root>
                        )}
                      />
                    </Field>
                  </SimpleGrid>
                </Stack>
              </Card.Body>
            </Card.Root>

            <PartnerContent />

            <Flex justify='flex-end' gap={3}>
              <Button
                type='button'
                variant='outline'
                onClick={() => navigate(`/${workspaceId}/settings`)}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                colorPalette='brand'
                loading={isSubmitting}
                loadingText='Saving...'
              >
                Save changes
              </Button>
            </Flex>
          </Stack>
        </form>
      </Container>
    </WorkspaceLayout>
  );
}
